import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";

interface Props {
  organizationId: number;
  setAlert: SetAlertType;
}

interface SelectedTagCategory {
  tag: string;
  category_id: number;
  brand_count: number;
  liked_count: number;
}

interface AvoidedTagCategory {
  category_id: number;
  avoided_tags: string[];
  reason: string;
}

interface UserSearchPreferenceRow {
  lastGeneratedAt: number | null;
  personaSummary: string | null;
  selectedTagCategories: SelectedTagCategory[];
  avoidedTagCategories: AvoidedTagCategory[];
}

interface FetchResponse {
  userSearchPreference: UserSearchPreferenceRow | null;
  categoryNames: Record<string, string>;
}

export default function AdminUserSearchPreferences({
  organizationId,
  setAlert,
}: Props) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [regenLoading, setRegenLoading] = useState(false);
  const [preference, setPreference] = useState<UserSearchPreferenceRow | null>(
    null,
  );
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>(
    {},
  );

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const res: FetchResponse = await fetcherAuth(
        getToken,
        `/api/admin/user-search-preferences/${organizationId}`,
        "GET",
      );
      setPreference(res.userSearchPreference);
      setCategoryNames(res.categoryNames || {});
    } catch (error) {
      setAlert(
        error?.message ||
          "An error occurred while fetching user search preferences",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenLoading(true);
    try {
      const res: FetchResponse = await fetcherAuth(
        getToken,
        `/api/admin/user-search-preferences/${organizationId}/generate`,
        "POST",
      );
      setPreference(res.userSearchPreference);
      setCategoryNames(res.categoryNames || {});
      setAlert("User search preferences regenerated", "success");
    } catch (error) {
      setAlert(
        error?.message ||
          "An error occurred while regenerating user search preferences",
        "error",
      );
    } finally {
      setRegenLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const categoryName = (id: number) =>
    categoryNames[String(id)] || `Category ${id}`;

  const boostsByCategory = useMemo(() => {
    if (!preference) return [];
    const groups = new Map<number, SelectedTagCategory[]>();
    for (const s of preference.selectedTagCategories) {
      const list = groups.get(s.category_id) ?? [];
      list.push(s);
      groups.set(s.category_id, list);
    }
    return Array.from(groups.entries())
      .map(([categoryId, items]) => ({
        categoryId,
        items: items.sort((a, b) => b.brand_count - a.brand_count),
      }))
      .sort((a, b) =>
        categoryName(a.categoryId).localeCompare(categoryName(b.categoryId)),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preference, categoryNames]);

  const formatDate = (epochSeconds: number | null) => {
    if (!epochSeconds) return "never";
    return new Date(epochSeconds * 1000).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h6">User Search Preferences</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Derived from this user's brand interactions (emails sent, brands saved,
        swipe likes/dislikes) and used to boost matching brands in their search
        results. Regenerates automatically as the user crosses activity
        thresholds — use the button below to force a fresh regeneration. Takes
        up to ~30s because it calls OpenAI.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <LoadingButton
          loading={regenLoading}
          onClick={handleRegenerate}
          variant="outlined"
          size="small"
        >
          Regenerate Search Preferences
        </LoadingButton>
        {preference && (
          <Typography variant="caption" color="text.secondary">
            Last generated: {formatDate(preference.lastGeneratedAt)}
          </Typography>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !preference ? (
        <Typography sx={{ my: 2 }} color="text.secondary">
          No search preferences have been generated for this user yet.
        </Typography>
      ) : (
        <Stack spacing={3} sx={{ mt: 1, maxWidth: 800 }}>
          <SectionCard title="Persona summary">
            {preference.personaSummary ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {preference.personaSummary}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No persona summary generated.
              </Typography>
            )}
          </SectionCard>

          <SectionCard
            title="Boosted tags by category"
            subtitle="Brands matching these tag + category combos rank higher in search."
          >
            {boostsByCategory.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {boostsByCategory.map(({ categoryId, items }) => (
                  <Box key={categoryId}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, fontWeight: 600 }}
                    >
                      {categoryName(categoryId)}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {items.map((s, idx) => (
                        <Chip
                          key={`${s.tag}-${idx}`}
                          label={s.tag}
                          size="small"
                          sx={{
                            bgcolor: "#dcfce7",
                            border: "1px solid #7bf1a8",
                            color: "#008236",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            title="Avoided tags by category"
            subtitle="Brands matching these tags within the category rank lower in search."
          >
            {preference.avoidedTagCategories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {preference.avoidedTagCategories.map((a, idx) => (
                  <Box key={`${a.category_id}-${idx}`}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {categoryName(a.category_id)}
                    </Typography>
                    {a.reason && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {a.reason}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {a.avoided_tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            bgcolor: "#fff7ed",
                            border: "1px solid #ffd6a8",
                            color: "#ca3500",
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </SectionCard>
        </Stack>
      )}
    </Box>
  );
}

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "rgba(1,67,57,0.1)",
        borderRadius: 2,
        bgcolor: "#fafaf6",
        p: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1.5, display: "block" }}
        >
          {subtitle}
        </Typography>
      )}
      <Box sx={{ mt: subtitle ? 0 : 1 }}>{children}</Box>
    </Box>
  );
}
